import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiArrowRight, FiCheck, FiCheckCircle, FiCornerUpLeft, FiMaximize2, FiRefreshCw, FiSearch, FiX, FiZoomIn, FiZoomOut } from 'react-icons/fi';
import { communityApi, readableError } from './api';
import { CLASS_LIST, COMMON_IDS, PREDATOR_IDS, classesForIds, className, displayClass } from './classList';

export function formatCaptureTime(value) {
  const time = value?.seconds ? value.seconds * 1000 : value?._seconds ? value._seconds * 1000 : value;
  const date = new Date(time);
  if (!time || Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    timeZone: 'Africa/Johannesburg',
  }).format(date);
}

function createRequestId() {
  return window.crypto?.randomUUID?.() || `review-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function isTypingTarget(target) {
  return Boolean(target?.closest?.('input, textarea, select, [contenteditable]:not([contenteditable="false"])'));
}

export function EvidenceImage({ task, onError, onLoad }) {
  const width = Number(task.frameW) || 1920;
  const height = Number(task.frameH) || 1080;
  const box = task.box;
  const boxStyle = box && {
    left: `${Math.max(0, box.x1) / width * 100}%`,
    top: `${Math.max(0, box.y1) / height * 100}%`,
    width: `${Math.max(0, Math.min(width, box.x2) - Math.max(0, box.x1)) / width * 100}%`,
    height: `${Math.max(0, Math.min(height, box.y2) - Math.max(0, box.y1)) / height * 100}%`,
  };
  return <div className="cr-evidence" style={{ aspectRatio: `${width} / ${height}` }}>
    <img
      src={task.imageUrl}
      alt={`Camera screenshot. Review the highlighted ${className(task.originalClassId).toLowerCase()} identification.`}
      onError={onError}
      onLoad={onLoad}
      draggable="false"
    />
    {box && <span data-testid="detection-box" className="cr-detection-box" style={boxStyle} aria-hidden="true" />}
  </div>;
}

export function ImageViewer({ task, onClose, onRefresh }) {
  const [zoom, setZoom] = useState(1);
  const [failed, setFailed] = useState(false);
  const dialog = useRef(null);
  useEffect(() => {
    const previousFocus = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.querySelector('button:not(:disabled)')?.focus();
    const handleKey = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab') return;
      const items = [...dialog.current.querySelectorAll('button:not(:disabled)')];
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', handleKey);
      previousFocus?.focus();
    };
  }, [onClose]);
  return <div className="cr-image-modal" role="dialog" aria-modal="true" aria-label="Enlarged screenshot" ref={dialog}>
    <div className="cr-image-toolbar">
      <span>Check the animal inside the orange box</span>
      <div>
        <button type="button" className="cr-icon-button" onClick={() => setZoom((value) => Math.max(1, value - 0.5))} disabled={zoom === 1} aria-label="Zoom out"><FiZoomOut /></button>
        <button type="button" className="cr-icon-button" onClick={() => setZoom((value) => Math.min(3, value + 0.5))} disabled={zoom === 3} aria-label="Zoom in"><FiZoomIn /></button>
        <button type="button" className="cr-icon-button" onClick={onClose} aria-label="Close enlarged screenshot"><FiX /></button>
      </div>
    </div>
    {failed && <p className="cr-error" role="alert">The full-size image could not load. <button type="button" className="cr-text-button" onClick={onRefresh}>Refresh image</button></p>}
    <div className="cr-image-scroll"><div className="cr-zoom-stage" style={{ width: `${zoom * 100}%` }}>
      <EvidenceImage task={task} enlarged onError={() => setFailed(true)} />
    </div></div>
  </div>;
}

export function AnimalPicker({ originalClassId, selectedClassId, onSelect, disabled = false, emptyMessage = 'No matching animals. You can still flag this as Wrong without choosing an animal.' }) {
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const searchText = search.trim().toLowerCase().replace(/[_-]/g, ' ');
  const matches = CLASS_LIST.filter((item) => `${displayClass(item)} ${item.name.replace(/[_-]/g, ' ')}`.toLowerCase().includes(searchText));
  const animalButtons = (items) => <div className="cr-animal-grid">
    {items.map((item) => <button
      className={`cr-animal-button ${selectedClassId === item.id ? 'is-selected' : ''}`}
      key={item.id}
      type="button"
      aria-pressed={selectedClassId === item.id}
      disabled={disabled || item.id === Number(originalClassId)}
      onClick={() => onSelect(item.id)}
    >
      <span>{displayClass(item)}{item.id === Number(originalClassId) && <small>Current ID</small>}</span>
      {selectedClassId === item.id && <FiCheck aria-hidden="true" />}
    </button>)}
  </div>;
  return <section className="cr-animal-picker" aria-labelledby="cr-animal-heading">
    <h2 id="cr-animal-heading">What animal is it? <span>(optional)</span></h2>
    <label className="cr-search">
      <FiSearch aria-hidden="true" />
      <input aria-label="Search all animals" placeholder="Search all animals…" value={search} onChange={(event) => setSearch(event.target.value)} type="search" disabled={disabled} />
    </label>
    {searchText || showAll ? <>
      <h3>{searchText ? `${matches.length} ${matches.length === 1 ? 'animal' : 'animals'} found` : `All ${CLASS_LIST.length} animals`}</h3>
      {matches.length ? animalButtons(matches) : <p className="cr-muted">{emptyMessage}</p>}
    </> : <>
      <h3>Common predators</h3>
      {animalButtons(classesForIds(PREDATOR_IDS))}
      <h3>Common animals</h3>
      {animalButtons(classesForIds(COMMON_IDS))}
    </>}
    {!searchText && <button className="cr-text-button cr-browse" type="button" onClick={() => setShowAll((value) => !value)} aria-expanded={showAll}>
      {showAll ? 'Show common animals' : `Browse all ${CLASS_LIST.length} animals`} <FiArrowRight aria-hidden="true" />
    </button>}
    {selectedClassId !== null && <button className="cr-text-button cr-clear-animal" type="button" disabled={disabled} onClick={() => onSelect(null)}>Clear suggested animal</button>}
  </section>;
}

export default function ReviewScreen({ api = communityApi, onCountChange, demo = false }) {
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [lastReview, setLastReview] = useState(null);
  const [retryPending, setRetryPending] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [verdict, setVerdict] = useState(null);
  const [correctedClassId, setCorrectedClassId] = useState(null);
  const [imageFailed, setImageFailed] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageGeneration, setImageGeneration] = useState(0);
  const [showImage, setShowImage] = useState(false);
  const mounted = useRef(false);
  const loadRevision = useRef(0);
  const busy = useRef(false);
  const attempt = useRef(null);
  const undoAttempt = useRef(null);
  const onCount = useRef(onCountChange);
  onCount.current = onCountChange;
  const currentTask = useRef(task);
  currentTask.current = task;

  const loadNext = useCallback(async ({ preserveAnswer = false } = {}) => {
    const revision = ++loadRevision.current;
    setLoading(true);
    setError('');
    try {
      let response;
      do {
        response = await api.next();
        if (!mounted.current || revision !== loadRevision.current) return;
      } while (!response.task && response.hasMore);
      const sameTask = response.task && response.task.id === currentTask.current?.id;
      const sameAssignment = sameTask && response.task.undoToken === currentTask.current?.undoToken;
      if (!preserveAnswer || !sameAssignment) {
        setVerdict(null);
        setCorrectedClassId(null);
      }
      if (!sameAssignment) { attempt.current = null; setRetryPending(false); }
      if (preserveAnswer && !sameTask) setNotice('That screenshot is no longer open. Your next available screenshot is ready.');
      else if (preserveAnswer && !sameAssignment) setNotice('This screenshot was restored by Undo. Please review it again.');
      setTask(response.task || null);
      setImageFailed(false);
      setImageLoaded(false);
      setImageGeneration((value) => value + 1);
      setLastReview(response.lastSubmission || null);
      if (undoAttempt.current?.submissionId !== response.lastSubmission?.submissionId) undoAttempt.current = null;
      onCount.current?.(Number(response.reviewedCount) || 0);
    } catch (requestError) {
      if (mounted.current && revision === loadRevision.current) setError(readableError(requestError));
    } finally {
      if (mounted.current && revision === loadRevision.current) setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    mounted.current = true;
    loadNext();
    return () => { mounted.current = false; };
  }, [loadNext]);

  const submitAnswer = useCallback(async (answer, correction = null) => {
    if (!task || !answer || busy.current || loading || imageFailed || !imageLoaded) return;
    busy.current = true;
    setSaving(true);
    setError('');
    setNotice('');
    if (!attempt.current || attempt.current.taskId !== task.id) attempt.current = { taskId: task.id, requestId: createRequestId(), verdict: answer, correctedClassId: answer === 'wrong' ? correction : null, ...(task.undoToken ? { undoToken: task.undoToken } : {}) };
    const payload = attempt.current;
    setVerdict(payload.verdict);
    setCorrectedClassId(payload.correctedClassId);
    try {
      const result = await api.submit(payload);
      if (!mounted.current) return;
      if (result.accepted === false && result.reason === 'submission-undone') {
        attempt.current = null; setRetryPending(false);
        setNotice('That review was undone in another session. Your current screenshot has been refreshed.');
        setTask(null);
        await loadNext();
        return;
      }
      if (result.accepted !== true && !(result.accepted === false && result.reason === 'task-closed')) {
        attempt.current = null; setRetryPending(false);
        setError('This review was not saved. Refresh its status before trying again.');
        return;
      }
      if (typeof result.reviewedCount === 'number') onCount.current?.(result.reviewedCount);
      setNotice(result.accepted === false && result.reason === 'task-closed'
        ? 'This screenshot was completed while you were reviewing it. Here’s the next one.'
        : result.alreadySubmitted ? 'Your review was already saved. Here’s the next screenshot.' : 'Review saved. Thank you!');
      // Discard only after acknowledgement, so a failed submission preserves the answer.
      if (result.lastSubmission) setLastReview(result.lastSubmission);
      attempt.current = null;
      setRetryPending(false);
      setTask(null);
      await loadNext();
      window.scrollTo?.({ top: 0, behavior: 'instant' });
    } catch (requestError) {
      if (mounted.current) {
        const uncertain = !requestError?.code || ['functions/unavailable', 'functions/deadline-exceeded', 'functions/internal'].includes(requestError.code);
        setRetryPending(uncertain);
        if (!uncertain) attempt.current = null;
        setError(readableError(requestError));
      }
    } finally {
      busy.current = false;
      if (mounted.current) setSaving(false);
    }
  }, [api, imageFailed, imageLoaded, loadNext, loading, task]);
  const undo = useCallback(async () => {
    if (!lastReview || !api.undo || busy.current || loading) return;
    busy.current = true;
    setUndoing(true); setError(''); setNotice('');
    if (!undoAttempt.current || undoAttempt.current.submissionId !== lastReview.submissionId) undoAttempt.current = { ...lastReview, requestId: createRequestId() };
    try {
      const result = await api.undo(undoAttempt.current);
      if (!mounted.current) return;
      if (result.undone !== true) {
        setError('The previous review could not be undone. Refresh its status before trying again.');
        return;
      }
      if (typeof result.reviewedCount === 'number') onCount.current?.(result.reviewedCount);
      attempt.current = null;
      setRetryPending(false);
      undoAttempt.current = null;
      setLastReview(result.lastSubmission || null);
      setVerdict(null); setCorrectedClassId(null);
      setImageFailed(false); setImageLoaded(false);
      setNotice('Last review undone. Please check this screenshot again.');
      setTask(null);
      await loadNext();
      window.scrollTo?.({ top: 0, behavior: 'instant' });
    } catch (requestError) {
      if (mounted.current) setError(readableError(requestError));
    } finally {
      busy.current = false;
      if (mounted.current) setUndoing(false);
    }
  }, [api, lastReview, loadNext, loading]);

  useEffect(() => {
    const handleShortcut = (event) => {
      if (event.defaultPrevented || event.repeat || event.isComposing || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey || isTypingTarget(event.target) || showImage || busy.current || loading) return;
      if (event.key.toLowerCase() === 's' && task && imageLoaded && !imageFailed && (!retryPending || verdict === 'correct')) {
        event.preventDefault();
        submitAnswer('correct');
      }
      if (event.key.toLowerCase() === 'u' && lastReview && !retryPending) {
        event.preventDefault();
        undo();
      }
    };
    document.addEventListener('keydown', handleShortcut);
    return () => document.removeEventListener('keydown', handleShortcut);
  }, [imageFailed, imageLoaded, lastReview, loading, retryPending, showImage, submitAnswer, task, undo, verdict]);

  const chooseVerdict = (value) => {
    if (busy.current || (retryPending && value !== verdict)) return;
    if (value === 'correct') { submitAnswer('correct'); return; }
    setVerdict(value);
    if (value !== 'wrong') setCorrectedClassId(null);
  };
  const chooseAnimal = (value) => {
    if (busy.current || retryPending) return;
    setCorrectedClassId(value);
    if (value !== null) setVerdict('wrong');
  };
  const submit = (event) => { event.preventDefault(); submitAnswer(verdict, correctedClassId); };
  const closeImage = useCallback(() => setShowImage(false), []);
  const refreshImage = () => { if (busy.current || loading) return; setShowImage(false); loadNext({ preserveAnswer: true }); };
  const operating = saving || undoing;
  const undoButton = <button type="button" className="cr-undo" aria-label="Undo last review" aria-keyshortcuts="U" disabled={!lastReview || operating || loading || retryPending} onClick={undo}><FiCornerUpLeft aria-hidden="true" /><span>{undoing ? 'Undoing…' : 'Undo'}</span><kbd aria-hidden="true">U</kbd></button>;

  return <main className="cr-main">
    <div className="cr-intro"><h1>Help spot a wrong ID</h1><p>A few minutes. One sighting at a time.</p></div>
    <div className="cr-announcement" role="status" aria-live="polite">{notice}</div>
    {demo && <p className="cr-demo-label">Design preview · Reviews stay in this browser and are not submitted.</p>}
    {loading ? <div className="cr-empty" role="status"><FiRefreshCw className="cr-spin" aria-hidden="true" /><h2>Finding your next screenshot…</h2><p>Only sightings you haven’t reviewed.</p></div>
      : !task ? <div className="cr-empty">
        {error ? <><h2>We couldn’t load your next screenshot</h2><p className="cr-error" role="alert">{error}</p></>
          : <><FiCheckCircle className="cr-empty-icon" aria-hidden="true" /><h2>You’re caught up</h2><p>You’ve reviewed all the screenshots currently available to you.<br />Come back soon for more sightings.</p></>}
        <div className="cr-empty-actions"><button className="cr-primary" onClick={loadNext} disabled={operating}><FiRefreshCw aria-hidden="true" /> {error ? 'Try again' : 'Check for new screenshots'}</button>{undoButton}</div>
      </div>
        : <form className="cr-review-layout" onSubmit={submit}>
          <section className="cr-photo-section" aria-label="Sighting screenshot">
            <p className="cr-photo-meta">{task.cameraLabel || 'PTZ camera'}{formatCaptureTime(task.capturedAt) && <> · {formatCaptureTime(task.capturedAt)} <span>SAST</span></>}</p>
            <div className="cr-photo-wrap">
              <EvidenceImage key={`${task.id}-${imageGeneration}`} task={task} onError={() => setImageFailed(true)} onLoad={() => { setImageFailed(false); setImageLoaded(true); }} />
              <button type="button" className="cr-enlarge" aria-label="Enlarge image" disabled={operating} onClick={() => setShowImage(true)}><FiMaximize2 aria-hidden="true" /><span>Enlarge image</span></button>
            </div>
            <p className="cr-photo-hint">Check the animal inside the orange box. Tap to enlarge if you need a closer look.</p>
            {imageFailed && <p className="cr-error" role="alert">This screenshot couldn’t load. <button type="button" className="cr-text-button" onClick={refreshImage}>Refresh image</button></p>}
          </section>
          <div className="cr-answer-panel">
            <span className="cr-eyebrow">AI identification</span>
            <div className="cr-ai-prediction">AI says: <strong>{className(task.originalClassId)}</strong></div>
            <div className="cr-verdicts" role="group" aria-label="Is the AI identification correct?">
              {[['correct', 'Correct'], ['wrong', 'Wrong'], ['unsure', 'Not sure']].map(([value, label]) => <button type="button" key={value}
                className={`cr-verdict ${verdict === value ? 'is-selected' : ''}`} aria-pressed={verdict === value} aria-keyshortcuts={value === 'correct' ? 'S' : undefined} title={value === 'correct' ? 'Correct — saves immediately (S)' : undefined} disabled={operating || (retryPending && value !== verdict) || (value === 'correct' && (!imageLoaded || imageFailed))} onClick={() => chooseVerdict(value)}>
                {label}{verdict === value && <FiCheck aria-hidden="true" />}
              </button>)}
            </div>
            <p className="cr-choice-hint">Correct saves immediately. <span className="cr-key-hint"><kbd>S</kbd> Correct · <kbd>U</kbd> Undo.</span><br />Choose another animal to mark this as wrong.</p>
            <AnimalPicker key={task.id} originalClassId={task.originalClassId} selectedClassId={correctedClassId} onSelect={chooseAnimal} disabled={operating || retryPending} />
            <div className="cr-submit-bar">
              {error && <p className="cr-error" role="alert">{error}</p>}
              {retryPending && <p className="cr-small cr-muted">Retry this review or refresh its status before choosing a different answer.</p>}
              {(error || retryPending) && <button type="button" className="cr-text-button cr-status-refresh" onClick={refreshImage} disabled={operating}>Refresh review status</button>}
              <div className="cr-submit-actions"><button className="cr-primary cr-submit" type="submit" disabled={!verdict || operating || imageFailed || !imageLoaded}>{saving ? 'Saving…' : 'Submit review'}</button>{undoButton}</div>
            </div>
          </div>
        </form>}
    {showImage && task && <ImageViewer task={task} onClose={closeImage} onRefresh={refreshImage} />}
  </main>;
}
